import { ReactComponent as DownloadIcon } from '@Icons/additional/download-gradient.svg';
import Card from 'antd/es/card/Card';
import Flex from 'antd/es/flex';
import Text from 'antd/es/typography/Text';
import { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  useGetTenderRepliesQuery,
  useGetTenderVendorAmountQuery,
} from '@Features/MaterialRequest/requests/TenderRequest/store/api/TenderRequestApiSlice';
import { getDocument, getFileTypeExt } from '@Features/utils/document';
import { LogoType } from '@Components/Base';
import { DocumentListResponse } from '@Types/api/form';
import {
  useDownloadDocumentQuery,
  useFormListMutation,
} from '@Store/api/dmsApiSlice';
import { List } from '../../../common/List';

import styles from './Quotation.module.css';

const FileCard: FC<{ filename: string; onClick: () => void }> = ({
  filename,
  onClick,
}) => {
  return (
    <Card className={styles.fileCard}>
      <Flex gap="16px" align="center">
        <Flex vertical flex="0 0 auto">
          <LogoType fileExt={getFileTypeExt(filename)} />
        </Flex>
        <Flex vertical flex="1 0 auto">
          <Text strong className={styles.fileCardTitle}>
            {filename}
          </Text>
          {/* <Text>48 MB</Text> */}
        </Flex>
        <Flex
          vertical
          flex="0 0 auto"
          onClick={onClick}
          style={{ cursor: 'pointer' }}
        >
          <DownloadIcon />
        </Flex>
      </Flex>
    </Card>
  );
};

const Quotation: FC<{ vendorId: number }> = ({ vendorId }) => {
  const { tenderId } = useParams();
  const [getFormList] = useFormListMutation();
  const { data: vendorAmountData } = useGetTenderVendorAmountQuery(
    { tenderId: tenderId!, vendorId },
    {
      skip: !tenderId || !vendorId,
    }
  );
  const [documentsInFolder, setDocumentsInFolder] =
    useState<DocumentListResponse[]>();

  const { data: repliesData } = useGetTenderRepliesQuery(tenderId!, {
    skip: !tenderId,
  });

  const currentVendor = repliesData?.find(
    (reply) => reply.vendorId === vendorId
  );

  useEffect(() => {
    if (vendorId) {
      const documentFolderPath = '';

      getFormList(documentFolderPath)
        .unwrap()
        .then((response) => {
          setDocumentsInFolder(response);
        });
    }
  }, [vendorId]);

  const findFileMCP =
    documentsInFolder &&
    currentVendor?.mcpAttachment &&
    getDocument(documentsInFolder, currentVendor.mcpAttachment);

  const findFileQuote =
    documentsInFolder &&
    currentVendor?.quoteAttachment &&
    getDocument(documentsInFolder, currentVendor.quoteAttachment);

  const { data: fileMCPUrl } = useDownloadDocumentQuery(
    (findFileMCP as DocumentListResponse)?.documentId || '',
    {
      skip: !(findFileMCP as DocumentListResponse)?.documentId,
    }
  );

  const { data: fileQUOTEUrl } = useDownloadDocumentQuery(
    (findFileQuote as DocumentListResponse)?.documentId || '',
    {
      skip: !(findFileQuote as DocumentListResponse)?.documentId,
    }
  );

  const handleDownload = (url: string | undefined, filePath: string): void => {
    if (url) {
      const a = document.createElement('a');
      a.href = url;
      a.download = filePath;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  };

  if (!vendorAmountData || !currentVendor) return null;

  return (
    <Flex vertical gap="16px">
      <List>
        <List.Item
          title="Selected Amount (without tax)"
          value={String(vendorAmountData.selectedAmountWithOutTax)}
        />
        <List.Item
          title="Total Amount (without tax)"
          value={String(vendorAmountData.selectedAmountWithOutTax)}
        />
        <List.Item title="Tax" value={String(vendorAmountData.tax)} />
        <List.Item
          title="Amount (with tax)"
          value={String(vendorAmountData.amountWithTax)}
        />
        <List.Item
          title="Payment Terms"
          value={String(vendorAmountData.paymentTerms)}
        />
      </List>
      <Flex gap="16px" justify="stretch">
        <Flex flex="1 0 auto">
          <FileCard
            filename={currentVendor.quoteAttachment}
            onClick={() =>
              handleDownload(fileQUOTEUrl, currentVendor.quoteAttachment)
            }
          />
        </Flex>
        <Flex flex="1 0 auto">
          <FileCard
            filename={currentVendor.mcpAttachment}
            onClick={() =>
              handleDownload(fileMCPUrl, currentVendor.mcpAttachment)
            }
          />
        </Flex>
      </Flex>
    </Flex>
  );
};

export default Quotation;
